import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { on, removeListener, run } from '../../helper/electron';
import './ScanningVisualization.scss';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// Define interface for individual track data within the progress event
interface TrackData {
  id: number;
  filePath: string;
  tags?: {
    genre?: string[];
    title?: string;
    artist?: string[];
    // Add other tags if needed
  };
  // Add other track properties if needed
}

// Define interface for the updated scan progress event data
interface ScanProgressData {
  numOfProcessed: number;
  total: number;
  folder: {
    id: string;
    path: string;
  };
  track?: TrackData; // Include the track data (optional as it might not be present initially)
}

const MAX_GENRES_DISPLAYED = 15;

const ScanningVisualization: React.FC = () => {
  // State for managing genre counts
  const [genreCounts, setGenreCounts] = useState<Map<string, number>>(
    new Map(),
  );
  const [currentFile, setCurrentFile] = useState<string>('');
  const [processedCount, setProcessedCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    // Check if we're in scanning mode (from parent component)
    const isInScanningMode =
      window.location.pathname.includes('/setup') ||
      document.querySelector('.scanning-section');

    if (isInScanningMode) {
      // We're in the wizard scanning mode, so we'll get data from the scan progress
      console.log(
        'ScanningVisualization: In scanning mode, will receive data from scan progress',
      );
      return;
    }

    // Otherwise, read existing data from the database
    console.log(
      'ScanningVisualization: Reading existing library data for visualization',
    );
    run('my-music/get-tracks-for-visualization');
  }, []);

  // Process scan progress events
  useEffect(() => {
    const handleScanProgress = (progressData: ScanProgressData) => {
      if (!progressData) return;

      // Update counts
      if (typeof progressData.numOfProcessed === 'number') {
        setProcessedCount(progressData.numOfProcessed);
      }
      if (typeof progressData.total === 'number') {
        setTotalCount(progressData.total);
      }

      // Update current file being processed
      if (progressData.track?.filePath) {
        const pathParts = progressData.track.filePath.split('/');
        setCurrentFile(pathParts[pathParts.length - 1] || '');
      }

      // Process genre from the track data
      if (progressData.track?.tags?.genre?.length) {
        const genres = progressData.track.tags.genre;
        setGenreCounts((prevCounts) => {
          const newCounts = new Map(prevCounts);
          genres.forEach((genre) => {
            if (genre && typeof genre === 'string') {
              // Normalize genre (e.g., lowercase)
              const normalizedGenre = genre.trim().toLowerCase();
              if (normalizedGenre) {
                newCounts.set(
                  normalizedGenre,
                  (newCounts.get(normalizedGenre) || 0) + 1,
                );
              }
            }
          });
          return newCounts;
        });
      }
    };

    const handleScanComplete = () => {
      console.log(
        'Scan complete, clearing visualization data (or keeping final state).',
      );
      // Optional: Clear counts or keep the final state
      // setGenreCounts(new Map());
      // setCurrentFile('');
      // setProcessedCount(0);
      // setTotalCount(0);
    };

    // Set up event listeners for both scanning mode and existing data mode
    on('my-music/rescan-folders', handleScanProgress); // For scanning mode
    on('my-music/scan-complete', handleScanComplete); // For scanning mode
    on('my-music/tracks-for-visualization', handleScanProgress); // For existing data mode
    on('my-music/visualization-complete', handleScanComplete); // For existing data mode

    // Clean up listeners on unmount
    return () => {
      removeListener('my-music/rescan-folders');
      removeListener('my-music/scan-complete');
      removeListener('my-music/tracks-for-visualization');
      removeListener('my-music/visualization-complete');
    };
  }, []); // Run only once on mount

  // Prepare data for the chart
  const sortedGenres = Array.from(genreCounts.entries())
    .sort(([, countA], [, countB]) => countB - countA) // Sort descending by count
    .slice(0, MAX_GENRES_DISPLAYED); // Take top N genres

  // Chart options for horizontal bar chart
  const chartOptions: any = {
    indexAxis: 'y' as const, // Make it a horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 200, // Slightly longer animation for smoother updates
    },
    scales: {
      x: {
        // X-axis represents the count
        beginAtZero: true,
        grid: {
          display: true, // Show grid lines for counts
          color: 'rgba(255, 255, 255, 0.1)', // Lighter grid lines for dark theme
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)', // Tick label color
        },
      },
      y: {
        // Y-axis represents the genre
        grid: {
          display: false, // Hide Y-axis grid lines
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.9)', // Genre label color
          // Corrected callback using index to get the label from chartData
          callback: function (index: number): string {
            // Type the index parameter explicitly
            const label: string = chartData.labels[index]; // Get label using index from chartData
            // Type the label explicitly
            if (label) {
              return label.charAt(0).toUpperCase() + label.slice(1);
            }
            return ''; // Return empty string if label is not found
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false, // No need for a legend with one dataset
      },
      tooltip: {
        enabled: true, // Enable tooltips to show exact counts
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.x !== null) {
              label += context.parsed.x;
            }
            return label;
          },
        },
      },
    },
  };

  // Chart data using sorted genres
  const chartData = {
    labels: sortedGenres.map(([genre]) => genre), // Genre names for labels
    datasets: [
      {
        label: 'Count', // Label for tooltip
        data: sortedGenres.map(([, count]) => count), // Counts for data
        backgroundColor: 'rgba(238, 117, 51, 0.7)', // Use theme primary color (orange)
        borderColor: 'rgba(238, 117, 51, 1)',
        borderWidth: 1,
        borderRadius: 2,
      },
    ],
  };

  return (
    <div className="scanning-visualization horizontal">
      {/* Display progress count */}
      {totalCount > 0 && (
        <div className="progress-counter">
          {processedCount} / {totalCount} files loaded
        </div>
      )}
      <div className="visualization-container">
        {sortedGenres.length > 0 ? (
          <Bar data={chartData} options={chartOptions} />
        ) : (
          <div className="placeholder-text">Loading genre data...</div>
        )}
      </div>
      {/* Display current file */}
      {currentFile && (
        <div className="current-file">
          <span className="pulse"></span>
          <span className="filename">{currentFile}</span>
        </div>
      )}
    </div>
  );
};

export default ScanningVisualization;
